import { log, warn, error } from '../../../utils/consoleLogger';

/**
 * Validates and provides default value for a prop
 */
export const validateProp = (value, defaultValue, propName, type = 'any') => {
  if (value === null || value === undefined) {
    if (defaultValue !== undefined) {
      warn(`ModelViewer: ${propName} is null/undefined, using default:`, defaultValue);
      return defaultValue;
    }
    return value;
  }

  // Type-specific validation
  switch (type) {
    case 'array':
      if (!Array.isArray(value)) {
        warn(`ModelViewer: ${propName} is not an array, using default:`, defaultValue);
        return defaultValue !== undefined ? defaultValue : [];
      }
      return value;
    case 'object':
      if (typeof value !== 'object' || Array.isArray(value)) {
        warn(`ModelViewer: ${propName} is not an object, using default:`, defaultValue);
        return defaultValue !== undefined ? defaultValue : {};
      }
      return value;
    case 'number':
      if (typeof value !== 'number' || isNaN(value)) {
        warn(`ModelViewer: ${propName} is not a valid number, using default:`, defaultValue);
        return defaultValue !== undefined ? defaultValue : 0;
      }
      return value;
    case 'string':
      if (typeof value !== 'string') {
        warn(`ModelViewer: ${propName} is not a string, using default:`, defaultValue);
        return defaultValue !== undefined ? defaultValue : '';
      }
      return value;
    case 'boolean':
      if (typeof value !== 'boolean') {
        warn(`ModelViewer: ${propName} is not a boolean, using default:`, defaultValue);
        return defaultValue !== undefined ? defaultValue : false;
      }
      return value;
    case 'function':
      if (typeof value !== 'function') {
        warn(`ModelViewer: ${propName} is not a function, using default`);
        return defaultValue;
      }
      return value;
    default:
      return value;
  }
};

/**
 * Safely get array element with validation
 */
export const getArrayElement = (array, index, propName, defaultValue = null) => {
  if (!Array.isArray(array)) {
    warn(`ModelViewer: ${propName} is not an array`);
    return defaultValue;
  }
  if (index < 0 || index >= array.length) {
    warn(`ModelViewer: ${propName}[${index}] is out of bounds (length: ${array.length})`);
    return defaultValue;
  }
  return array[index];
};

/**
 * Safely get nested property with validation
 */
export const getNestedProperty = (obj, path, propName, defaultValue = undefined) => {
  if (!obj || typeof obj !== 'object') {
    warn(`ModelViewer: Cannot access ${propName}.${path} - object is invalid`);
    return defaultValue;
  }
  
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      warn(`ModelViewer: Cannot access ${propName}.${path} - path broken at ${key}`);
      return defaultValue;
    }
    current = current[key];
  }
  
  return current !== undefined ? current : defaultValue;
};

/**
 * Validate productSettings with defaults
 */
export const validateProductSettings = (productSettings) => {
  if (!productSettings || typeof productSettings !== 'object' || Array.isArray(productSettings)) {
    warn('ModelViewer: productSettings is invalid, using empty object');
    return {};
  }

  const defaults = {
    showDimensions: false,
    showDimensionsAtStart: false,
    simpleDimensions: false,
    advancedDimensions: false,
    isBaked: false,
    meshInteractions: false,
    reversibleAnimation: false,
    ThreeSixtyViewToggle: false,
    isDisableScrolling: false,
    pure3D: false,
    sceneBackgroundColour: 'white',
    units: 'cm',
    numDigits: 0,
    dimensionBoxOffset: [0, 0, 0],
    fitZoom: 0.6,
    zoomClose: 0.3,
    maxZoomFactor: 2,
    startCamYawAngle: 25,
    startCamPitchAngle: 60,
    tiltAngle: 0,
    panAngle: 0,
    environmentRotation: [0, 90, 0],
    interactiveMeshes: [],
    animationMapping: {},
  };

  // Merge with defaults, but only log warnings for critical missing properties
  const validated = { ...defaults, ...productSettings };

  // Validate nested objects/arrays
  if (!Array.isArray(validated.interactiveMeshes)) {
    warn('ModelViewer: productSettings.interactiveMeshes is not an array, using empty array');
    validated.interactiveMeshes = [];
  }

  if (!validated.animationMapping || typeof validated.animationMapping !== 'object' || Array.isArray(validated.animationMapping)) {
    warn('ModelViewer: productSettings.animationMapping is invalid, using empty object');
    validated.animationMapping = {};
  }

  if (!Array.isArray(validated.dimensionBoxOffset) || validated.dimensionBoxOffset.length !== 3) {
    warn('ModelViewer: productSettings.dimensionBoxOffset is invalid, using [0, 0, 0]');
    validated.dimensionBoxOffset = [0, 0, 0];
  }

  if (!Array.isArray(validated.environmentRotation) || validated.environmentRotation.length !== 3) {
    warn('ModelViewer: productSettings.environmentRotation is invalid, using [0, 90, 0]');
    validated.environmentRotation = [0, 90, 0];
  }

  return validated;
};

/**
 * Validate numeric prop with range
 */
export const validateNumericProp = (value, propName, min = -Infinity, max = Infinity, defaultValue = 0) => {
  const num = Number(value);
  if (isNaN(num)) {
    warn(`ModelViewer: ${propName} is not a valid number, using default:`, defaultValue);
    return defaultValue;
  }
  if (num < min || num > max) {
    warn(`ModelViewer: ${propName} (${num}) is out of range [${min}, ${max}], clamping`);
    return Math.max(min, Math.min(max, num));
  }
  return num;
};
